import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useRef } from "react";

interface StudioSessionToolbarProps {
  mode: "parse" | "train";
  session: StudioSessionMessage;
  isUploading: boolean;
  isExtracting: boolean;
  appliedTemplateMode: boolean;
  templateDetails: StudioTemplateDetailsMessage | null;
  statusLabel: string;
  statusColor: "default" | "success" | "error";
  materializing: boolean;
  saving: boolean;
  sourceName: string;
  missingRequiredLabels: string[];
  saveBlocked: boolean;
  onReplace: (file: File) => void;
  onStartOver: () => void;
  onOpenSaveDialog: (dialogMode: "create" | "variant") => void;
  onMaterialize: () => void;
}

export default function StudioSessionToolbar({
  mode,
  session,
  isUploading,
  isExtracting,
  appliedTemplateMode,
  templateDetails,
  statusLabel,
  statusColor,
  materializing,
  saving,
  sourceName,
  missingRequiredLabels,
  saveBlocked,
  onReplace,
  onStartOver,
  onOpenSaveDialog,
  onMaterialize,
}: StudioSessionToolbarProps) {
  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  const blockReasons = (): string => {
    const reasons: string[] = [];
    if (!sourceName) reasons.push("Source not set");
    if (missingRequiredLabels.length > 0) {
      reasons.push(`Missing: ${missingRequiredLabels.join(", ")}`);
    }
    return reasons.join(" · ");
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{
        paddingX: 2,
        paddingY: 1.5,
        borderBottom: "1px solid #E0E0E0",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {appliedTemplateMode ? "Auto-Parsed" : "Template Draft"}
        </Typography>
        {appliedTemplateMode && templateDetails && (
          <Chip
            size="small"
            color="primary"
            label={templateDetails.name}
            variant="outlined"
          />
        )}
        <Chip size="small" label={statusLabel} color={statusColor} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {session.documentPath?.split("/").pop() ?? "—"}
        </Typography>
      </Stack>

      <Tooltip title="Replace with a different PDF">
        <span>
          <Button
            variant="text"
            size="small"
            disabled={isExtracting || isUploading}
            onClick={() => replaceInputRef.current?.click()}
          >
            Replace document
          </Button>
        </span>
      </Tooltip>
      <input
        ref={replaceInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onReplace(f);
          if (replaceInputRef.current) replaceInputRef.current.value = "";
        }}
      />
      <Button
        variant="outlined"
        size="small"
        disabled={isExtracting || isUploading}
        onClick={onStartOver}
      >
        Discard
      </Button>
      {appliedTemplateMode ? (
        <>
          <Tooltip title="Save current bindings as a new template (variant of the applied one)">
            <span>
              <Button
                variant="outlined"
                size="small"
                disabled={
                  isExtracting ||
                  isUploading ||
                  materializing ||
                  saving ||
                  !session.draftExtraction ||
                  !templateDetails
                }
                onClick={() => onOpenSaveDialog("variant")}
              >
                Save as new template
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            disabled={
              isExtracting ||
              isUploading ||
              materializing ||
              !session.draftExtraction
            }
            onClick={onMaterialize}
          >
            {materializing ? "Creating…" : "Create Entities"}
          </Button>
        </>
      ) : (
        <>
          <Tooltip title={blockReasons()}>
            <span>
              <Button
                variant={mode === "parse" ? "outlined" : "contained"}
                size="small"
                disabled={isExtracting || isUploading || saveBlocked}
                onClick={() => onOpenSaveDialog("create")}
              >
                Save Template
              </Button>
            </span>
          </Tooltip>
          {mode === "parse" && (
            <Tooltip title={blockReasons()}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    isExtracting || isUploading || materializing || saveBlocked
                  }
                  onClick={onMaterialize}
                >
                  {materializing ? "Creating…" : "Create Entities"}
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      )}
    </Stack>
  );
}
